import { type RankingItem } from '../types';

/**
 * Record a win for the winner (simple +1 point system)
 */
export function recordWin(
  items: RankingItem[],
  item1Id: string,
  item2Id: string,
  winnerId: string
): RankingItem[] {
  return items.map((item) => {
    if (item.id === winnerId) {
      return {
        ...item,
        wins: item.wins + 1,
        comparisons: item.comparisons + 1,
      };
    } else if (item.id === item1Id || item.id === item2Id) {
      return {
        ...item,
        losses: item.losses + 1,
        comparisons: item.comparisons + 1,
      };
    }
    return item;
  });
}

/**
 * Generate all unique pairwise comparisons
 */
export function generateComparisons(items: RankingItem[]): Array<[string, string]> {
  const comparisons: Array<[string, string]> = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      comparisons.push([items[i].id, items[j].id]);
    }
  }
  return comparisons;
}

/**
 * Sort items by wins (descending), then by name for stability
 */
export function sortByRank(items: RankingItem[]): RankingItem[] {
  return [...items].sort((a, b) => {
    if (b.wins !== a.wins) {
      return b.wins - a.wins;
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * Detect items with tied win counts
 */
export function detectTies(items: RankingItem[]): Map<number, string[]> {
  const ties = new Map<number, string[]>();
  const scoreGroups = new Map<number, string[]>();

  // Group items by win count
  items.forEach((item) => {
    if (!scoreGroups.has(item.wins)) {
      scoreGroups.set(item.wins, []);
    }
    scoreGroups.get(item.wins)!.push(item.id);
  });

  // Find groups with more than one item (ties)
  scoreGroups.forEach((itemIds, score) => {
    if (itemIds.length > 1) {
      ties.set(score, itemIds);
    }
  });

  return ties;
}

/**
 * Generate pairwise comparisons for tie-breaking round
 */
export function generateTieBreakerPairs(tiedItemIds: string[]): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  for (let i = 0; i < tiedItemIds.length; i++) {
    for (let j = i + 1; j < tiedItemIds.length; j++) {
      pairs.push([tiedItemIds[i], tiedItemIds[j]]);
    }
  }
  return pairs;
}

/**
 * Calculate raw score table
 */
export function getRawScores(items: RankingItem[]): Record<string, number> {
  const scores: Record<string, number> = {};
  items.forEach((item) => {
    scores[item.name] = item.wins;
  });
  return scores;
}
